
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    ArrowUpRight,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Compass,
    Heart,
    Hotel,
    ImageOff,
    MapPin,
    RefreshCw,
    Search,
    ShieldCheck,
    SlidersHorizontal,
    Sparkles,
    Star,
} from "lucide-react";

const API_URL = "https://dubaibudgetivehotels.com/api/hotels";
const IMAGE_URL = "https://uaediscountedhotels.com/uploads/hotels/";

const WISHLIST_KEY = "wishlist";
const HOTEL_WISHLIST_KEY = "dubai_hotel_wishlist";

const containerClass =
    "mx-auto w-full max-w-[1536px] px-4 sm:px-6 lg:px-8";

const primaryButton =
    "inline-flex items-center justify-center gap-2 rounded-xl bg-[#168B43] px-5 py-3.5 text-[13px] font-extrabold text-white transition hover:bg-[#087D37]";

function getHotelId(hotel) {
    return String(hotel?.id ?? hotel?.slug ?? "");
}

function readWishlist(key) {
    try {
        const value = JSON.parse(localStorage.getItem(key) || "[]");
        return Array.isArray(value) ? value : [];
    } catch {
        return [];
    }
}

function getHotelImage(image) {
    if (!image) return "";

    const value =
        typeof image === "object"
            ? image.url || image.path || image.image || ""
            : image;

    if (typeof value !== "string") return "";

    if (/^https?:\/\//i.test(value)) return value;
    if (value.startsWith("//")) return `https:${value}`;
    if (value.startsWith("/")) return value;

    return `${IMAGE_URL}${value.replace(/^uploads\/hotels\//, "")}`;
}

function stripHtml(value) {
    if (!value) return "";

    return String(value)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function HotelImage({ hotel }) {
    const [failed, setFailed] = useState(false);
    const src = getHotelImage(hotel.image);

    useEffect(() => {
        setFailed(false);
    }, [src]);

    return (
        <div className="relative h-full min-h-[240px] overflow-hidden bg-[#E8F7EE]">
            {src && !failed ? (
                <img
                    src={src}
                    alt={hotel.name || "Dubai hotel"}
                    loading="lazy"
                    onError={() => setFailed(true)}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
            ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#064E3B] to-[#17324D] text-white/70">
                    <ImageOff size={36} strokeWidth={1.5} />
                    <span className="text-xs font-bold">Hotel image unavailable</span>
                </div>
            )}

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#032F28]/35 via-transparent to-transparent" />
        </div>
    );
}

function HotelSkeleton() {
    return (
        <div className="overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white lg:grid lg:grid-cols-[38%_62%]">
            <div className="h-[250px] animate-pulse bg-[#E2EEE6] lg:h-full lg:min-h-[300px]" />
            <div className="space-y-5 p-6 sm:p-7">
                <div className="h-4 w-28 animate-pulse rounded bg-[#E2EEE6]" />
                <div className="h-7 w-3/4 animate-pulse rounded bg-[#E2EEE6]" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-[#E2EEE6]" />
                <div className="h-4 w-full animate-pulse rounded bg-[#E2EEE6]" />
                <div className="h-4 w-4/5 animate-pulse rounded bg-[#E2EEE6]" />
                <div className="flex justify-between pt-5">
                    <div className="h-8 w-28 animate-pulse rounded bg-[#E2EEE6]" />
                    <div className="h-11 w-32 animate-pulse rounded-xl bg-[#E2EEE6]" />
                </div>
            </div>
        </div>
    );
}

function HotelCard({ hotel, index, saved, onWishlist, onOpen }) {
    const price = Number(hotel.price_per_night);
    const hasPrice =
        hotel.price_per_night !== null &&
        hotel.price_per_night !== undefined &&
        hotel.price_per_night !== "" &&
        Number.isFinite(price);

    const priceLabel = hasPrice
        ? `AED ${Math.round(price).toLocaleString("en-AE")}`
        : "Contact for price";

    const rating = Number(hotel.rating);
    const hasRating =
        hotel.rating !== null &&
        hotel.rating !== undefined &&
        hotel.rating !== "" &&
        Number.isFinite(rating);

    return (
        <article className="group overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white transition-all duration-300 hover:-translate-y-0.5 hover:border-[#B9DFC5] hover:shadow-[0_20px_50px_rgba(6,78,59,0.09)] lg:grid lg:grid-cols-[38%_62%]">
            {/* HOTEL IMAGE */}
            <div className="relative min-h-[240px] lg:min-h-[300px]">
                <button
                    type="button"
                    onClick={onOpen}
                    aria-label={`View ${hotel.name}`}
                    className="absolute inset-0 block h-full w-full text-left"
                >
                    <HotelImage hotel={hotel} />
                </button>

                <div className="pointer-events-none absolute bottom-4 left-4 rounded-full bg-white/95 px-4 py-2 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#064E3B] shadow-sm">
                    Dubai Stay {String(index + 1).padStart(2, "0")}
                </div>

                <button
                    type="button"
                    onClick={onWishlist}
                    aria-label={
                        saved
                            ? `Remove ${hotel.name} from wishlist`
                            : `Save ${hotel.name} to wishlist`
                    }
                    aria-pressed={saved}
                    className={`absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-md transition hover:scale-110 ${saved ? "text-[#E84C68]" : "text-[#17324D]"
                        }`}
                >
                    <Heart
                        size={20}
                        fill={saved ? "currentColor" : "none"}
                    />
                </button>
            </div>

            {/* HOTEL INFORMATION */}
            <div className="flex min-w-0 flex-col p-5 sm:p-7">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.1em] text-[#168B43]">
                        <MapPin size={15} />
                        {hotel.location || "Dubai, UAE"}
                    </span>

                    {hasRating && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFF8E8] px-3 py-1.5 text-xs font-extrabold text-[#A56C14]">
                            <Star size={14} fill="currentColor" />
                            {rating} / 5
                        </span>
                    )}
                </div>

                <button
                    type="button"
                    onClick={onOpen}
                    className="mt-4 text-left text-[23px] font-extrabold leading-tight tracking-tight text-[#17324D] transition hover:text-[#168B43] sm:text-[27px]"
                >
                    {hotel.name || "Dubai Hotel"}
                </button>

                <p className="mt-3 line-clamp-2 text-[13px] leading-7 text-[#718096]">
                    {stripHtml(hotel.short_description) ||
                        "Discover this Dubai hotel, explore its location and find accommodation for your next stay."}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-2 rounded-full border border-[#E0ECE4] bg-[#F5FAF7] px-3 py-2 text-[11px] font-bold text-[#526B5D]">
                        <Hotel size={14} className="text-[#168B43]" />
                        Hotel Stay
                    </span>

                    <span className="inline-flex items-center gap-2 rounded-full border border-[#E0ECE4] bg-[#F5FAF7] px-3 py-2 text-[11px] font-bold text-[#526B5D]">
                        <MapPin size={14} className="text-[#168B43]" />
                        Dubai
                    </span>
                </div>

                <div className="mt-auto flex flex-wrap items-end justify-between gap-5 border-t border-[#E8F0EB] pt-6">
                    <div>
                        <p className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#899A90]">
                            Starting from / night
                        </p>
                        <p className="mt-1 text-[24px] font-extrabold tracking-tight text-[#17324D]">
                            {priceLabel}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={onOpen}
                        className={primaryButton}
                    >
                        View Hotel
                        <ArrowRight size={17} />
                    </button>
                </div>
            </div>
        </article>
    );
}

export default function HotelSearch() {
    const navigate = useNavigate();
    const location = useLocation();
    const searchData = location.state || {};

    const destination = searchData.destination || "Dubai, UAE";

    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [wishlist, setWishlist] = useState([]);
    const [sortBy, setSortBy] = useState("default");

    const syncWishlist = useCallback(() => {
        const modern = readWishlist(HOTEL_WISHLIST_KEY);
        const legacy = readWishlist(WISHLIST_KEY);

        const merged = [...modern];
        const seen = new Set(modern.map(getHotelId));

        legacy.forEach((item) => {
            const id = getHotelId(item);

            if (id && !seen.has(id)) {
                merged.push(item);
                seen.add(id);
            }
        });

        setWishlist(merged);
    }, []);

    useEffect(() => {
        syncWishlist();

        window.addEventListener("storage", syncWishlist);
        window.addEventListener("hotel-wishlist-updated", syncWishlist);

        return () => {
            window.removeEventListener("storage", syncWishlist);
            window.removeEventListener(
                "hotel-wishlist-updated",
                syncWishlist
            );
        };
    }, [syncWishlist]);

    const toggleWishlist = (hotel) => {
        const id = getHotelId(hotel);
        if (!id) return;

        const exists = wishlist.some((item) => getHotelId(item) === id);

        const updated = exists
            ? wishlist.filter((item) => getHotelId(item) !== id)
            : [...wishlist, hotel];

        try {
            localStorage.setItem(
                HOTEL_WISHLIST_KEY,
                JSON.stringify(updated)
            );

            localStorage.setItem(
                WISHLIST_KEY,
                JSON.stringify(updated)
            );

            setWishlist(updated);

            window.dispatchEvent(
                new Event("hotel-wishlist-updated")
            );
        } catch (storageError) {
            console.error("Unable to update wishlist:", storageError);
        }
    };

    useEffect(() => {
        let active = true;

        const fetchHotels = async () => {
            setLoading(true);
            setError(false);

            try {
                const response = await axios.get(API_URL, {
                    params: {
                        destination: searchData.destination,
                    },
                });

                if (!active) return;

                const data = response.data;

                setHotels(Array.isArray(data) ? data : []);
            } catch (fetchError) {
                console.error("Hotel search failed:", fetchError);

                if (active) {
                    setError(true);
                    setHotels([]);
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchHotels();

        return () => {
            active = false;
        };
    }, [searchData.destination, retryCount]);

    const sortedHotels = useMemo(() => {
        const items = [...hotels];

        if (sortBy === "price-low") {
            items.sort(
                (a, b) =>
                    (Number(a.price_per_night) || Infinity) -
                    (Number(b.price_per_night) || Infinity)
            );
        }

        if (sortBy === "price-high") {
            items.sort(
                (a, b) =>
                    (Number(b.price_per_night) || 0) -
                    (Number(a.price_per_night) || 0)
            );
        }

        if (sortBy === "rating") {
            items.sort(
                (a, b) =>
                    (Number(b.rating) || 0) -
                    (Number(a.rating) || 0)
            );
        }

        return items;
    }, [hotels, sortBy]);

    const openHotel = (hotel) => {
        if (!hotel.slug) return;

        navigate(`/hotels/${encodeURIComponent(hotel.slug)}`);

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const resultLabel = loading
        ? "Searching hotels..."
        : `${hotels.length} ${hotels.length === 1 ? "hotel" : "hotels"
        } found`;

    return (
        <main className="min-h-screen bg-[#F5FAF7] text-[#17324D]">
            {/* =====================================================
          HERO BANNER
      ===================================================== */}

            <section className="relative isolate overflow-hidden bg-[#064E3B] pt-28 text-white sm:pt-32">
                <div className="pointer-events-none absolute -right-20 -top-20 -z-10 h-[420px] w-[420px] rounded-full border border-white/10" />
                <div className="pointer-events-none absolute right-10 top-10 -z-10 h-[290px] w-[290px] rounded-full border border-white/10" />
                <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28] via-[#064E3B] to-[#08794B]" />

                <div className={`${containerClass} pb-14 pt-4 sm:pb-20`}>
                    <nav
                        aria-label="Breadcrumb"
                        className="flex flex-wrap items-center gap-2 text-xs font-semibold text-white/70"
                    >
                        <Link to="/" className="hover:text-white">
                            Home
                        </Link>

                        <ChevronRight size={14} />

                        <Link to="/hotels" className="hover:text-white">
                            Hotels
                        </Link>

                        <ChevronRight size={14} />

                        <span className="text-[#B6F2CB]">
                            Search Results
                        </span>
                    </nav>

                    <div className="mt-10 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
                        <div className="max-w-[850px]">
                            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB]">
                                <Sparkles size={15} />
                                Find Your Perfect Stay
                            </div>

                            <h1 className="mt-5 text-[40px] font-extrabold leading-[1.12] tracking-tight sm:text-[55px] lg:text-[65px]">
                                Discover Hotels
                                <br />
                                <span className="text-[#A5E8B8]">
                                    In {destination}.
                                </span>
                            </h1>

                            <p className="mt-5 max-w-[640px] text-[14px] leading-8 text-white/75 sm:text-base">
                                Explore comfortable Dubai hotels, compare
                                accommodation options and find the right
                                place for your next trip.
                            </p>

                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white">
                                    <MapPin size={15} className="text-[#A5E8B8]" />
                                    {destination}
                                </span>

                                <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white">
                                    <Hotel size={15} className="text-[#A5E8B8]" />
                                    {resultLabel}
                                </span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => navigate("/hotels")}
                            className="inline-flex w-fit items-center gap-3 rounded-xl border border-white/25 bg-white/10 px-5 py-3.5 text-sm font-extrabold text-white transition hover:bg-white/20"
                        >
                            <ArrowLeft size={17} />
                            Change Search
                        </button>
                    </div>
                </div>
            </section>

            {/* =====================================================
          SEARCH SUMMARY
      ===================================================== */}

            <section className="relative z-10 -mt-7">
                <div className={containerClass}>
                    <div className="flex flex-col gap-5 rounded-[20px] border border-[#DCEAE1] bg-white p-5 shadow-[0_15px_45px_rgba(6,78,59,0.08)] sm:p-6 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl bg-[#E8F7EE] p-3 text-[#168B43]">
                                <MapPin size={25} />
                            </div>

                            <div>
                                <p className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                                    Your Destination
                                </p>

                                <h2 className="mt-1 text-[20px] font-extrabold text-[#17324D] sm:text-[24px]">
                                    {destination}
                                </h2>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-4">
                            <span className="inline-flex items-center gap-2 text-[13px] font-bold text-[#526B5D]">
                                <span className="h-2.5 w-2.5 rounded-full bg-[#168B43]" />
                                {resultLabel}
                            </span>

                            <button
                                type="button"
                                onClick={() => navigate("/hotels")}
                                className="inline-flex items-center gap-2 rounded-xl bg-[#E8F7EE] px-4 py-3 text-xs font-extrabold text-[#168B43] transition hover:bg-[#D5F0DF]"
                            >
                                <Search size={16} />
                                Search Again
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* =====================================================
          SEARCH RESULTS
      ===================================================== */}

            <section className="pb-20 pt-14 sm:pb-24 sm:pt-16">
                <div className={containerClass}>
                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_300px] xl:gap-10">
                        {/* RESULTS COLUMN */}
                        <div className="min-w-0">
                            <div className="mb-8 flex flex-wrap items-end justify-between gap-5">
                                <div>
                                    <p className="text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#168B43]">
                                        Explore Your Options
                                    </p>

                                    <h2 className="mt-3 text-[30px] font-extrabold tracking-tight text-[#17324D] sm:text-[40px]">
                                        Available{" "}
                                        <span className="text-[#168B43]">
                                            Hotels.
                                        </span>
                                    </h2>

                                    <p className="mt-3 text-sm leading-7 text-[#718096]">
                                        Browse the available hotels and choose
                                        the stay that suits your plans.
                                    </p>
                                </div>

                                {!loading && !error && hotels.length > 0 && (
                                    <div className="flex items-center gap-2 rounded-xl border border-[#DCEAE1] bg-white px-3 py-2">
                                        <SlidersHorizontal
                                            size={17}
                                            className="text-[#168B43]"
                                        />

                                        <select
                                            value={sortBy}
                                            onChange={(event) =>
                                                setSortBy(event.target.value)
                                            }
                                            aria-label="Sort hotels"
                                            className="max-w-[190px] cursor-pointer bg-transparent py-1.5 text-[12px] font-bold text-[#17324D] outline-none"
                                        >
                                            <option value="default">Recommended</option>
                                            <option value="price-low">Price: Low to High</option>
                                            <option value="price-high">Price: High to Low</option>
                                            <option value="rating">Highest Rated</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            {loading ? (
                                <div className="space-y-6">
                                    {[1, 2, 3, 4].map((item) => (
                                        <HotelSkeleton key={item} />
                                    ))}
                                </div>
                            ) : error ? (
                                <div className="rounded-[24px] border border-[#DCEAE1] bg-white px-6 py-16 text-center sm:py-20">
                                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] bg-red-50 text-red-500">
                                        <RefreshCw size={34} />
                                    </div>

                                    <h3 className="mt-6 text-[27px] font-extrabold text-[#17324D]">
                                        Unable To Load Hotels
                                    </h3>

                                    <p className="mx-auto mt-4 max-w-[440px] text-sm leading-8 text-[#718096]">
                                        Something went wrong while loading the
                                        hotels. Please try again.
                                    </p>

                                    <button
                                        type="button"
                                        onClick={() => setRetryCount((value) => value + 1)}
                                        className={`${primaryButton} mt-7`}
                                    >
                                        <RefreshCw size={17} />
                                        Try Again
                                    </button>
                                </div>
                            ) : sortedHotels.length === 0 ? (
                                <div className="rounded-[24px] border border-[#DCEAE1] bg-white px-6 py-16 text-center sm:py-20">
                                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[24px] bg-[#E8F7EE] text-[#168B43]">
                                        <Hotel size={36} />
                                    </div>

                                    <h3 className="mt-6 text-[27px] font-extrabold text-[#17324D]">
                                        No Hotels Found
                                    </h3>

                                    <p className="mx-auto mt-4 max-w-[460px] text-sm leading-8 text-[#718096]">
                                        We couldn't find hotels for this destination.
                                        Try another search or explore all our Dubai hotels.
                                    </p>

                                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => navigate("/hotels")}
                                            className={primaryButton}
                                        >
                                            Explore Hotels
                                            <ArrowRight size={17} />
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => navigate("/")}
                                            className="rounded-xl border border-[#DCEAE1] bg-white px-5 py-3.5 text-[13px] font-extrabold text-[#17324D] transition hover:bg-[#F5FAF7]"
                                        >
                                            Back To Home
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {sortedHotels.map((hotel, index) => (
                                        <HotelCard
                                            key={getHotelId(hotel) || index}
                                            hotel={hotel}
                                            index={index}
                                            saved={wishlist.some(
                                                (item) => getHotelId(item) === getHotelId(hotel)
                                            )}
                                            onWishlist={() => toggleWishlist(hotel)}
                                            onOpen={() => openHotel(hotel)}
                                        />
                                    ))}

                                    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-[#DCEAE1] bg-white px-5 py-4">
                                        <p className="text-xs font-semibold text-[#718096]">
                                            Showing {sortedHotels.length} of {hotels.length} hotels
                                        </p>

                                        <Link
                                            to="/wishlist"
                                            className="inline-flex items-center gap-2 text-xs font-extrabold text-[#168B43] hover:underline"
                                        >
                                            <Heart size={16} />
                                            View Saved Hotels
                                            <ArrowRight size={15} />
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* =================================================
                SIDEBAR
            ================================================= */}

                        <aside className="space-y-6 lg:sticky lg:top-24">
                            {/* DISCOVERY CARD */}
                            <div className="relative overflow-hidden rounded-[22px] bg-[#064E3B] p-7 text-white">
                                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full border border-white/10" />

                                <div className="relative">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/20 bg-white/10 text-[#B6F2CB]">
                                        <Compass size={25} />
                                    </div>

                                    <p className="mt-6 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#B6F2CB]">
                                        Discover Dubai
                                    </p>

                                    <h3 className="mt-3 text-[27px] font-extrabold leading-tight text-white">
                                        Find Your Next
                                        <br />
                                        Dubai Stay.
                                    </h3>

                                    <p className="mt-4 text-[13px] leading-7 text-white/75">
                                        Explore hotels in Dubai, compare your
                                        options and save the properties you love.
                                    </p>

                                    <button
                                        type="button"
                                        onClick={() => navigate("/hotels")}
                                        className="mt-7 inline-flex items-center gap-2 rounded-xl bg-[#168B43] px-5 py-3.5 text-xs font-extrabold text-white transition hover:bg-[#087D37]"
                                    >
                                        Browse All Hotels
                                        <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* WHY CHOOSE US */}
                            <div className="rounded-[22px] border border-[#DCEAE1] bg-white p-6">
                                <p className="text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                                    Why Choose Us
                                </p>

                                <h3 className="mt-3 text-[21px] font-extrabold text-[#17324D]">
                                    A Better Way To Explore.
                                </h3>

                                <div className="mt-6 space-y-6">
                                    {[
                                        {
                                            icon: ShieldCheck,
                                            title: "Explore With Confidence",
                                            description:
                                                "Review hotel details before choosing your stay.",
                                        },
                                        {
                                            icon: MapPin,
                                            title: "Convenient Locations",
                                            description:
                                                "Discover accommodation in Dubai neighborhoods.",
                                        },
                                        {
                                            icon: Heart,
                                            title: "Save Your Favorites",
                                            description:
                                                "Keep the hotels you love in your wishlist.",
                                        },
                                    ].map(({ icon: Icon, title, description }) => (
                                        <div key={title} className="flex items-start gap-3">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                                <Icon size={19} />
                                            </div>

                                            <div>
                                                <h4 className="text-[13px] font-extrabold text-[#17324D]">
                                                    {title}
                                                </h4>

                                                <p className="mt-1 text-xs leading-6 text-[#718096]">
                                                    {description}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* SEARCH AGAIN */}
                            <div className="rounded-[22px] border border-[#DCEAE1] bg-white p-6">
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE] text-[#168B43]">
                                    <Search size={21} />
                                </div>

                                <h3 className="mt-4 text-[18px] font-extrabold text-[#17324D]">
                                    Looking For Something Else?
                                </h3>

                                <p className="mt-3 text-xs leading-7 text-[#718096]">
                                    Change your destination and discover
                                    more hotel options.
                                </p>

                                <button
                                    type="button"
                                    onClick={() => navigate("/hotels")}
                                    className="mt-5 inline-flex items-center gap-2 text-xs font-extrabold text-[#168B43] hover:gap-3"
                                >
                                    Start A New Search
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
        </main>
    );
}
